import {
	conditionTests,
	conditionValueIsEmpty,
	showOncePerSessionTriggers,
	getDeviceType,
	getCssSelectorTextContent,
	getJsFromString,
	testCondition,
	parseUrlString,
} from '@/rules';
import { getCookie, timeInSeconds } from '@/utils';
import Box from './Box';
import Indicator from './Indicator';

import useRemainingTime from '../hooks/useRemainingTime';
import useRemainingSessionTime from '../hooks/useRemainingSessionTime';

const conditionNiceNames = {
	trigger: 'Trigger by',
	scrollpos: 'Scroll position',
	percentage: 'Percentage',
	pause: 'Is paused',
	number_of_pages: 'Number of pages',
	location: 'URL',
	referrer: 'Referrer',
	date: 'Date',
	clock: 'Time',
	type: 'New or returning',
	devices: 'Device',
	cookie: 'Cookies',
	css_selector: 'HTML elements',
	js: 'JavaScript variables',
	time: 'Time on page',
	time_in_session: 'Time in session',
	form_events: 'Form interaction',
	event_listeners: 'Page interaction',
	time_after_valid: 'Time after valid',
};

const triggerNiceNames = {
	passive: 'by button',
	proactive: 'proactive',
	exit: 'on page leave',
};

export function filterOrConditionArray(conditionArray = [], testFn = () => {}) {
	return conditionArray.filter(orCondition => {
		if (orCondition instanceof Array) {
			return orCondition.every(andCondition => testFn(andCondition)) !== true;
		}

		return testFn(orCondition) !== true;
	});
}
function formatNestedOrAnd(arr, fn) {
	return `Does not match one of ${arr.map(nestedArr => nestedArr.map(obj => fn(obj)).join(' and ')).join(' or ')}`;
}

const conditionInvalidHandlers = {
	percentage: value => `Outside of target group, percentage set: ${value}`,
	pause: () => 'The condition is currently paused',
	scrollpos: value =>
		`Needs to be ${value.px}${String(value.px).indexOf('%') === -1 ? ' pixels' : ''} from ${value.from}`,
	number_of_pages: (value, { pageCount }) =>
		`Needs to be ${value.check === 'min' ? 'at least' : 'at most'} ${
			value.amount
		} pages. Currently visited pages: ${pageCount}`,
	location: value => {
		const url = parseUrlString(document.location.href);
		const operatorMap = {
			exists: 'contains',
			'does not exist': 'does not contain',
		};
		const failedLocations = filterOrConditionArray(value, locationCondition => {
			return testCondition({
				value1: url,
				value2: parseUrlString(locationCondition.value),
				operator: operatorMap[locationCondition.operator] ?? locationCondition.operator,
				regex: locationCondition.regex,
			});
		});

		return formatNestedOrAnd(failedLocations, obj => `${obj.value} ${obj.operator}`);
	},
	referrer: (value, { previousLocation }) => {
		const url = parseUrlString(previousLocation);

		//location uses 'exists', 'does not exist' for contains/does not contain
		const operatorMap = {
			exists: 'contains',
			'does not exist': 'does not contain',
		};
		const failedReferrers = filterOrConditionArray(value, referrerCondition => {
			if (typeof referrerCondition === 'string') {
				return testCondition({
					value1: url,
					value2: parseUrlString(referrerCondition),
					operator: 'contains',
					regex: false,
				});
			}
			return testCondition({
				value1: url,
				value2: parseUrlString(referrerCondition.value),
				operator: operatorMap[referrerCondition.operator] ?? referrerCondition.operator,
				regex: referrerCondition.regex,
			});
		});

		return formatNestedOrAnd(failedReferrers, obj => `${obj.value} ${obj.operator}`);
	},
	devices: value => {
		const device = getDeviceType().phone ? 'phone' : getDeviceType().tablet ? 'tablet' : 'desktop';

		return `Needs to be: ${value.join(', ')}, current device: ${device}`;
	},
	type: value => `Only ${value} vistors`,
	date: value => {
		const currentDate = `${new Date().getDate()}/${new Date().getMonth() + 1}/${new Date().getFullYear()}`;
		switch (value.operator) {
			case 'between':
				return `Needs to be ${value.operator} ${value.date} and ${value.date2}, current date: ${currentDate}`;
			case 'exactly':
				return `Needs to be ${value.operator} ${value.date}, current date: ${currentDate}`;
			default:
				return `Needs to be ${value.operator} than ${value.date}, current date: ${currentDate}`;
		}
	},
	clock: value => {
		const now = new Date();
		const hours = `0${now.getHours()}`.slice(-2);
		const minutes = `0${now.getMinutes()}`.slice(-2);
		const currentTime = `${hours}:${minutes}`;
		switch (value.operator) {
			case 'between':
				return `Needs to be ${value.operator} ${value.time} and ${value.time2}, current time: ${currentTime}`;
			case 'exactly':
				return `Needs to be ${value.operator} ${value.time}, current time: ${currentTime}`;
			default:
				return `Needs to be ${value.operator} than ${value.time}, current time: ${currentTime}`;
		}
	},
	cookie: value => {
		const failedCookies = filterOrConditionArray(value, cookieCondition =>
			testCondition({
				value1: getCookie(cookieCondition.name),
				operator: cookieCondition.operator,
				value2: cookieCondition.value,
			})
		);
		return formatNestedOrAnd(failedCookies, obj => `${obj.name} ${obj.operator} ${obj.value}`);
	},
	css_selector: value => {
		const failedSelectors = filterOrConditionArray(value, cssSelectorCondition =>
			testCondition({
				value1: getCssSelectorTextContent(cssSelectorCondition.name),
				operator: cssSelectorCondition.operator,
				value2: cssSelectorCondition.value,
			})
		);

		return formatNestedOrAnd(failedSelectors, obj => `${obj.name} ${obj.operator} ${obj.value}`);
	},
	js: value => {
		const failedJsVars = filterOrConditionArray(value, jsCondition => {
			return testCondition({
				value1: getJsFromString(jsCondition.name),
				operator: jsCondition.operator,
				value2: jsCondition.value,
			});
		});
		return formatNestedOrAnd(failedJsVars, obj => `${obj.name} ${obj.operator} ${obj.value}`);
	},
	event_listeners: (value, { eventListeners }) => {
		const failedEventListeners = filterOrConditionArray(value, eventListenerCondition =>
			testCondition({
				value1: eventListeners?.[eventListenerCondition.selector],
				operator: eventListenerCondition.operator,
				value2: eventListenerCondition.event,
			})
		);
		const operatorMap = {
			contains: 'has',
			'does not contain': 'has not',
		};
		const eventNameMap = {
			click: 'been clicked',
			onmouseover: 'been hovered',
			input: 'received input',
		};
		return formatNestedOrAnd(
			failedEventListeners,
			obj => `${obj.selector} ${operatorMap[obj.operator]} ${eventNameMap[obj.event]}`
		);
	},
	form_events: (value, { formEvents, surveyData }) => {
		const failedFormEvents = filterOrConditionArray(value, formEventCondition =>
			testCondition({
				value1: formEvents?.[formEventCondition.formKey],
				operator: formEventCondition.operator,
				value2: formEventCondition.event,
			})
		);
		const operatorMap = {
			contains: 'has been',
			'does not contain': 'has not been',
		};

		const eventNameMap = {
			feedback_sent: 'completed',
			shown: 'shown',
			hidden: 'closed',
		};
		return formatNestedOrAnd(failedFormEvents, obj => {
			const formName = surveyData[obj.formKey]?.name;
			return `${formName} ${operatorMap[obj.operator]} ${eventNameMap[obj.event]}`;
		});
	},

	time: (value, { pageTime }) => {
		return pageTime.remaining + ' seconds remaining';
	},
	time_after_valid: (value, { validTime }) => {
		return validTime.remaining + ' seconds remaining';
	},
	time_in_session: (value, { sessionTime }) => {
		return sessionTime.remaining + ' seconds remaining';
	},
};

function parseConditionValues({
	key,
	value,
	pageCount,
	pageTime,
	sessionTime,
	eventListeners,
	formEvents,
	surveyData,
	validTime,
}) {
	const tooltip = conditionInvalidHandlers[key]?.(value, {
		pageCount,
		pageTime,
		sessionTime,
		eventListeners,
		formEvents,
		surveyData,
		validTime,
	});
	return tooltip;
}

export default function Condition({ rule, ruleInstance, condition, index, valid, surveyData }) {
	const timeOnPageValid = timeInSeconds() - ruleInstance?.getTimeOnPageState(index, 'time') >= Number(condition.time);
	const timeAfterValid =
		timeInSeconds() - ruleInstance?.getTimeOnPageState(index, 'time_after_valid') >= Number(condition.time);

	const pageTime = useRemainingTime({
		time: condition.time,
		timerStarted: ruleInstance?.getTimeOnPageState(index),
		valid: timeOnPageValid,
	});

	const validTime = useRemainingTime({
		time: condition.time_after_valid,
		timerStarted: ruleInstance?.getTimeOnPageState(index, 'time_after_valid'),
		valid: timeAfterValid,
	});

	const sessionTime = useRemainingSessionTime({
		index,
		ruleInstance: ruleInstance,
		timeInSession: condition.time_in_session,
	});

	const alreadyTriggered =
		showOncePerSessionTriggers.indexOf(condition.trigger) > -1 &&
		ruleInstance?.getConditionState(index, state => state.triggered);

	const tested = Object.keys(condition)
		.filter(conditionKey => {
			const conditionTestFn = conditionTests[conditionKey];
			const conditionValue = condition[conditionKey];
			if (conditionValueIsEmpty(conditionValue)) {
				return false;
			}

			if (typeof conditionTestFn !== 'function') {
				return false;
			}

			//dont show 'pause true' could be confusing
			if (conditionKey === 'pause' && conditionTestFn(conditionValue)) {
				return false;
			}

			return true;
		})
		.map(conditionKey => {
			const conditionTestFn = conditionTests[conditionKey];
			const conditionValue = condition[conditionKey];
			const state = {
				pageCount: ruleInstance?.getState(state => state.pageCount),
				returning: ruleInstance?.getState(state => state.returning),
				percentage: ruleInstance?.getConditionState(index, state => state.percentage),
				timeOnPage: ruleInstance?.getTimeOnPageState(index, 'time'),
				timeAfterValid: ruleInstance?.getTimeOnPageState(index, 'time_after_valid'),
				sessionTime: ruleInstance?.getConditionState(index, state => state.sessionTime),
				eventListeners: ruleInstance?.getConditionState(index, state => state.domEvents),
				formEvents: ruleInstance?.getConditionState(index, state => state.formEvents),
				referrer: ruleInstance?.globalListenerStore?.getPreviousLocation(),
			};

			const valid = conditionTestFn(conditionValue, {
				rule,
				state,
			});

			return {
				name: conditionNiceNames[conditionKey],
				conditionKey,
				conditionValue,
				conditionValid: valid,
			};
		});

	return (
		<Box name="condition-container">
			<Box name="condition-title-container">
				<Indicator active={valid} small mr={1} />
				<Box name="condition-title">
					<Box>Condition set {index + 1}</Box>
					<Box name="session">{condition.session} day session</Box>
				</Box>
			</Box>

			<Box name="condition-rule-container">
				<Indicator
					small
					active={
						condition.trigger === 'passive' ||
						(!alreadyTriggered && condition.trigger === 'proactive') ||
						(alreadyTriggered && valid)
					}
					mr={1}
				/>
				<Box>
					<Box name="condition-rule">Trigger {triggerNiceNames[condition.trigger]}</Box>
					{alreadyTriggered && !valid && <Box name="invalid-reason">Already triggered within session</Box>}
				</Box>
			</Box>
			{tested.map(({ name, conditionValid, conditionKey, conditionValue }) => {
				return (
					<Box name="condition-rule-container">
						<Indicator small active={conditionValid} mr={1} />
						<Box>
							<Box name="condition-rule">{name}</Box>
							{!conditionValid && (
								<Box name="invalid-reason">
									{parseConditionValues({
										key: conditionKey,
										value: conditionValue,
										pageCount: ruleInstance?.getState(state => state.pageCount),
										pageTime,
										sessionTime,
										validTime,
										eventListeners: ruleInstance?.getConditionState(index, state => state.domEvents),
										formEvents: ruleInstance?.getConditionState(index, state => state.formEvents),
										surveyData,
										previousLocation: ruleInstance?.globalListenerStore?.getPreviousLocation(),
									})}
								</Box>
							)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}
